import React, { useState, useRef, useEffect } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Modal, Button, Slider, Spin } from "antd";
import heic2any from "heic2any";

/* ============================= */
/*  HEIC → JPEG + Resize Helper  */
/* ============================= */

async function prepareImage(file) {
  let workingFile = file;

  // HEIC konvertieren
  if (
    file.type === "image/heic" ||
    file.name.toLowerCase().endsWith(".heic")
  ) {
    const blob = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.85
    });

    workingFile = new File(
      [blob],
      file.name.replace(/\.heic$/i, ".jpg"),
      { type: "image/jpeg" }
    );
  }

  return URL.createObjectURL(workingFile);
}

/* ============================= */
/*  A4 Optimiertes Cropping      */
/* ============================= */

function getCroppedImg(image, crop) {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  const sourceWidth = crop.width * scaleX;
  const sourceHeight = crop.height * scaleY;

  // 📄 A4 quer bei 150 DPI
  const A4_WIDTH = 1754;
  const A4_HEIGHT = 1240;

  // 🧾 Reservierter Platz für Kopf/Fuß
  const RESERVED_HEIGHT = 450;

  const MAX_WIDTH = A4_WIDTH;
  const MAX_HEIGHT = A4_HEIGHT - RESERVED_HEIGHT;

  const widthRatio = MAX_WIDTH / sourceWidth;
  const heightRatio = MAX_HEIGHT / sourceHeight;

  const resizeFactor = Math.min(widthRatio, heightRatio, 1);

  const targetWidth = sourceWidth * resizeFactor;
  const targetHeight = sourceHeight * resizeFactor;

  canvas.width = targetWidth;
  canvas.height = targetHeight;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    targetWidth,
    targetHeight
  );

  return canvas.toDataURL("image/jpeg", 0.88);
}

/* ============================= */
/*        Component              */
/* ============================= */

export default function ReceiptCropper({
  file,
  open,
  onClose,
  onSave
}) {
  const imgRef = useRef(null);

  const [imageSrc, setImageSrc] = useState(null);
  const [loading, setLoading] = useState(false);

  const [crop, setCrop] = useState({
    unit: "px",
    x: 50,
    y: 50,
    width: 300,
    height: 400
  });

  const [completedCrop, setCompletedCrop] = useState(null);

  /* ============================= */
  /*  Bild vorbereiten (HEIC etc.) */
  /* ============================= */

  useEffect(() => {
    if (!open || !file) return;

    let isMounted = true;

    const load = async () => {
      setLoading(true);
      const src = await prepareImage(file);
      if (isMounted) {
        setImageSrc(src);
        setLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [file, open]);

  /* ============================= */
  /*  Initial Crop setzen         */
  /* ============================= */

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;

    const initialCrop = {
      unit: "px",
      x: width * 0.05,
      y: height * 0.05,
      width: width * 0.9,
      height: height * 0.9
    };

    setCrop(initialCrop);
    setCompletedCrop(initialCrop);
  };

  /* ============================= */
  /*  Speichern                   */
  /* ============================= */

  const handleSave = () => {
    const cropToUse = completedCrop || crop;

    if (!cropToUse || !imgRef.current) return;

    const croppedImage = getCroppedImg(
      imgRef.current,
      cropToUse
    );

    onSave(croppedImage);
    onClose();
  };

  const isTabletLandscape =
    window.innerWidth > 768 && window.innerHeight < 900;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={isTabletLandscape ? 900 : "100%"}
      style={{ top: isTabletLandscape ? 40 : 0 }}
      bodyStyle={{ padding: 12 }}
      maskClosable={false}
      destroyOnClose
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" />
          <div style={{ marginTop: 20 }}>
            Bild wird vorbereitet...
          </div>
        </div>
      ) : (
        <>
          <div style={{ textAlign: "center" }}>
            <ReactCrop
              crop={crop}
              onChange={(c) => {
                if (c.width > 30 && c.height > 30) {
                  setCrop(c);
                }
              }}
              onComplete={(c) => setCompletedCrop(c)}
              keepSelection
              minWidth={100}
              minHeight={100}
            >
              {imageSrc && (
                <img
                  ref={imgRef}
                  src={imageSrc}
                  onLoad={onImageLoad}
                  alt="Beleg"
                  style={{ maxWidth: "100%" }}
                />
              )}
            </ReactCrop>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 20
            }}
          >
            <Button size="large" onClick={onClose}>
              Abbrechen
            </Button>

            <Button
              size="large"
              type="primary"
              onClick={handleSave}
              style={{ touchAction: "manipulation" }}
            >
              Zuschneiden
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}